import React from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Alert, AlertDescription } from '../../components/ui/alert';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { FileText, Plus, Edit, Copy, Eye, Save, X, AlertCircle, CheckCircle, Tag, Bold, Italic, Underline, List, ListOrdered, AlignLeft, AlignCenter, AlignRight, Link as LinkIcon, Heading1, Heading2, Quote, FileDown, Printer, Share2, ExternalLink, Mail, MessageSquare } from 'lucide-react';
import { sanitizeHtml } from '../../utils/sanitizeHtml';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const _API = `${BACKEND_URL}/api`;

export default function TemplatesModals({ ctx }) {
  const { applyFormat, categories, editingTemplate, editorRef, formData, handleAddTag, handleCopyShareLink, handleDownloadPDF, handleEdit, handleGenerateDocument, handlePrintTemplate, handleRemoveTag, handleSave, handleSendShare, handleShareTemplate, insertHeading, insertVariable, newCategory, newTag, newType, previewTemplate, setFormData, setNewCategory, setNewTag, setNewType, setShareEmail, setShareMessage, setShowCustomCategory, setShowCustomType, setShowModal, setShowPreview, setShowShareDialog, shareEmail, shareMessage, shareTemplate, showCustomCategory, showCustomType, showModal, showPreview, showShareDialog, templates: templates, types, variables } = ctx;
  return (
    <>
      {/* Modal de création/édition */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-6xl max-h-[90vh] overflow-y-auto">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FileText className="h-5 w-5 text-blue-600" />
                {editingTemplate ? 'Éditer le Template' : 'Nouveau Template'}
              </CardTitle>
              <CardDescription>
                Créez ou modifiez un modèle de document pour les experts
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Nom */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Nom du template <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Ex: Rapport d'expertise technique"
                />
              </div>

              {/* Description */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Description
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  rows={2}
                  className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Description du template..."
                />
              </div>

              {/* Catégorie et Type */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Catégorie */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Catégorie <span className="text-red-500">*</span>
                  </label>
                  {showCustomCategory ? (
                    <div className="flex gap-2">
                      <input
                        type="text"
                        value={newCategory}
                        onChange={(e) => setNewCategory(e.target.value)}
                        className="flex-1 px-4 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="Nouvelle catégorie..."
                      />
                      <Button
                        onClick={() => {
                          if (newCategory) {
                            setFormData({ ...formData, category: newCategory });
                            setShowCustomCategory(false);
                            setNewCategory('');
                          }
                        }}
                        size="sm"
                      >
                        <CheckCircle className="h-4 w-4" />
                      </Button>
                      <Button
                        onClick={() => setShowCustomCategory(false)}
                        size="sm"
                        variant="outline"
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      <select
                        value={formData.category}
                        onChange={(e) => {
                          if (e.target.value === '__custom__') {
                            setShowCustomCategory(true);
                          } else {
                            setFormData({ ...formData, category: e.target.value });
                          }
                        }}
                        className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      >
                        <option value="">-- Sélectionner --</option>
                        {categories.all.map(cat => (
                          <option key={cat} value={cat}>{cat}</option>
                        ))}
                        <option value="__custom__">+ Nouvelle catégorie</option>
                      </select>
                    </div>
                  )}
                </div>

                {/* Type */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Type de document <span className="text-red-500">*</span>
                  </label>
                  {showCustomType ? (
                    <div className="flex gap-2">
                      <input
                        type="text"
                        value={newType}
                        onChange={(e) => setNewType(e.target.value)}
                        className="flex-1 px-4 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="Nouveau type..."
                      />
                      <Button
                        onClick={() => {
                          if (newType) {
                            setFormData({ ...formData, type: newType });
                            setShowCustomType(false);
                            setNewType('');
                          }
                        }}
                        size="sm"
                      >
                        <CheckCircle className="h-4 w-4" />
                      </Button>
                      <Button
                        onClick={() => setShowCustomType(false)}
                        size="sm"
                        variant="outline"
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                  ) : (
                    <select
                      value={formData.type}
                      onChange={(e) => {
                        if (e.target.value === '__custom__') {
                          setShowCustomType(true);
                        } else {
                          setFormData({ ...formData, type: e.target.value });
                        }
                      }}
                      className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="">-- Sélectionner --</option>
                      {types.all.map(type => (
                        <option key={type} value={type}>{type}</option>
                      ))}
                      <option value="__custom__">+ Nouveau type</option>
                    </select>
                  )}
                </div>
              </div>

              {/* Tags */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Tags
                </label>
                <div className="flex gap-2 mb-2">
                  <input
                    type="text"
                    value={newTag}
                    onChange={(e) => setNewTag(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && handleAddTag()}
                    className="flex-1 px-4 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Ajouter un tag..."
                  />
                  <Button onClick={handleAddTag} size="sm">
                    <Plus className="h-4 w-4" />
                  </Button>
                </div>
                <div className="flex flex-wrap gap-2">
                  {formData.tags.map(tag => (
                    <Badge key={tag} className="bg-purple-600 text-white font-semibold">
                      <Tag className="mr-1 h-3 w-3" />
                      {tag}
                      <button
                        onClick={() => handleRemoveTag(tag)}
                        className="ml-2 hover:text-red-600"
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </Badge>
                  ))}
                </div>
              </div>

              {/* Variables disponibles */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="flex items-start gap-2 mb-3">
                  <AlertCircle className="h-5 w-5 text-blue-600 mt-0.5" />
                  <div className="flex-1">
                    <h4 className="font-semibold text-blue-900 mb-2">Variables disponibles</h4>
                    <p className="text-sm text-blue-800 mb-3">
                      Cliquez sur une variable pour l&apos;insérer dans le template
                    </p>
                    <div className="space-y-2">
                      {Object.entries(variables).map(([category, vars]) => (
                        <details key={category} className="bg-white rounded p-2">
                          <summary className="cursor-pointer font-medium text-sm text-slate-700 capitalize">
                            {category}
                          </summary>
                          <div className="flex flex-wrap gap-2 mt-2">
                            {vars.map(variable => (
                              <button
                                key={variable}
                                onClick={() => insertVariable(variable)}
                                className="px-2 py-1 bg-blue-700 text-white rounded text-xs hover:bg-blue-200 transition-colors"
                              >
                                {variable}
                              </button>
                            ))}
                          </div>
                        </details>
                      ))}
                    </div>
                  </div>
                </div>
              </div>

              {/* Éditeur HTML personnalisé */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Contenu du template <span className="text-red-500">*</span>
                </label>
                
                {/* Barre d'outils de formatage */}
                <div className="border border-slate-300 rounded-t-lg bg-slate-50 p-2 flex flex-wrap gap-1">
                  <button
                    type="button"
                    onClick={() => insertHeading(1)}
                    className="p-2 hover:bg-slate-200 rounded"
                    title="Titre 1"
                  >
                    <Heading1 className="h-4 w-4" />
                  </button>
                  <button
                    type="button"
                    onClick={() => insertHeading(2)}
                    className="p-2 hover:bg-slate-200 rounded"
                    title="Titre 2"
                  >
                    <Heading2 className="h-4 w-4" />
                  </button>
                  <div className="w-px bg-slate-300 mx-1" />
                  <button
                    type="button"
                    onClick={() => applyFormat('bold')}
                    className="p-2 hover:bg-slate-200 rounded"
                    title="Gras"
                  >
                    <Bold className="h-4 w-4" />
                  </button>
                  <button
                    type="button"
                    onClick={() => applyFormat('italic')}
                    className="p-2 hover:bg-slate-200 rounded"
                    title="Italique"
                  >
                    <Italic className="h-4 w-4" />
                  </button>
                  <button
                    type="button"
                    onClick={() => applyFormat('underline')}
                    className="p-2 hover:bg-slate-200 rounded"
                    title="Souligné"
                  >
                    <Underline className="h-4 w-4" />
                  </button>
                  <div className="w-px bg-slate-300 mx-1" />
                  <button
                    type="button"
                    onClick={() => applyFormat('justifyLeft')}
                    className="p-2 hover:bg-slate-200 rounded"
                    title="Aligner à gauche"
                  >
                    <AlignLeft className="h-4 w-4" />
                  </button>
                  <button
                    type="button"
                    onClick={() => applyFormat('justifyCenter')}
                    className="p-2 hover:bg-slate-200 rounded"
                    title="Centrer"
                  >
                    <AlignCenter className="h-4 w-4" />
                  </button>
                  <button
                    type="button"
                    onClick={() => applyFormat('justifyRight')}
                    className="p-2 hover:bg-slate-200 rounded"
                    title="Aligner à droite"
                  >
                    <AlignRight className="h-4 w-4" />
                  </button>
                  <div className="w-px bg-slate-300 mx-1" />
                  <button
                    type="button"
                    onClick={() => applyFormat('insertUnorderedList')}
                    className="p-2 hover:bg-slate-200 rounded"
                    title="Liste à puces"
                  >
                    <List className="h-4 w-4" />
                  </button>
                  <button
                    type="button"
                    onClick={() => applyFormat('insertOrderedList')}
                    className="p-2 hover:bg-slate-200 rounded"
                    title="Liste numérotée"
                  >
                    <ListOrdered className="h-4 w-4" />
                  </button>
                  <div className="w-px bg-slate-300 mx-1" />
                  <button
                    type="button"
                    onClick={() => {
                      const url = prompt('Entrez l\'URL du lien:');
                      if (url) applyFormat('createLink', url);
                    }}
                    className="p-2 hover:bg-slate-200 rounded"
                    title="Insérer un lien"
                  >
                    <LinkIcon className="h-4 w-4" />
                  </button>
                  <button
                    type="button"
                    onClick={() => applyFormat('formatBlock', 'blockquote')}
                    className="p-2 hover:bg-slate-200 rounded"
                    title="Citation"
                  >
                    <Quote className="h-4 w-4" />
                  </button>
                </div>

                {/* Zone d'édition */}
                <div
                  ref={editorRef}
                  contentEditable
                  onInput={(e) => setFormData({ ...formData, html_content: e.currentTarget.innerHTML })}
                  onFocus={() => editorRef.current.focus()}
                  /* eslint-disable-next-line react/no-danger -- contentEditable WYSIWYG: sanitisation se fait au moment du rendu côté preview/diff, pas en édition (sinon le curseur saute) */
                  dangerouslySetInnerHTML={{ __html: formData.html_content }}
                  className="w-full min-h-[500px] px-4 py-3 border border-t-0 border-slate-300 rounded-b-lg focus:outline-none focus:ring-2 focus:ring-blue-500 overflow-auto"
                  style={{
                    fontFamily: 'Arial, sans-serif',
                    fontSize: '14px',
                    lineHeight: '1.6'
                  }}
                />
                
                <p className="text-xs text-slate-500 mt-2">
                  💡 Sélectionnez du texte et utilisez les boutons de formatage ci-dessus. Cliquez sur une variable pour l&apos;insérer.
                </p>
              </div>

              {/* Boutons d'action */}
              <div className="flex gap-3 justify-end pt-4 border-t">
                <Button
                  onClick={() => setShowModal(false)}
                  variant="outline"
                >
                  <X className="mr-2 h-4 w-4" />
                  Annuler
                </Button>
                <Button
                  onClick={handleGenerateDocument}
                  variant="outline"
                  className="flex items-center gap-2"
                >
                  <FileDown className="h-4 w-4" />
                  Générer HTML
                </Button>
                <Button
                  onClick={handleSave}
                  className="bg-blue-600 hover:bg-blue-700"
                >
                  <Save className="mr-2 h-4 w-4" />
                  {editingTemplate ? 'Mettre à jour' : 'Créer'}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Dialog de prévisualisation */}
      <Dialog open={showPreview} onOpenChange={setShowPreview}>
        <DialogContent className="max-w-4xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Eye className="w-5 h-5 text-green-600" />
              Prévisualisation - {previewTemplate?.name}
            </DialogTitle>
            <DialogDescription className="flex items-center gap-2 flex-wrap">
              <Badge className="bg-purple-600 text-white font-semibold">{previewTemplate?.category}</Badge>
              <Badge className="bg-blue-600 text-white font-semibold">{previewTemplate?.type}</Badge>
              {previewTemplate?.is_active ? (
                <Badge className="bg-green-600 text-white font-semibold">Actif</Badge>
              ) : (
                <Badge className="bg-gray-400 font-semibold">Inactif</Badge>
              )}
            </DialogDescription>
          </DialogHeader>
          
          {previewTemplate && (
            <div className="space-y-4">
              {previewTemplate.description && (
                <Alert className="bg-green-50 border-green-200">
                  <AlertDescription className="text-green-800">
                    {previewTemplate.description}
                  </AlertDescription>
                </Alert>
              )}
              
              {/* Tags */}
              {previewTemplate.tags && previewTemplate.tags.length > 0 && (
                <div className="flex flex-wrap gap-2">
                  {previewTemplate.tags.map(tag => (
                    <Badge key={tag} className="bg-blue-600 text-white font-semibold">
                      <Tag className="mr-1 h-3 w-3" />
                      {tag}
                    </Badge>
                  ))}
                </div>
              )}
              
              <div className="border rounded-lg p-6 bg-white min-h-[300px]">
                <div 
                  className="prose prose-sm max-w-none"
                  dangerouslySetInnerHTML={{ __html: sanitizeHtml(previewTemplate.html_content || '<p class="text-gray-400 italic">Aucun contenu</p>') }}
                />
              </div>
              
              <div className="flex justify-between items-center pt-4 border-t">
                <div className="text-xs text-gray-500">
                  Créé le: {new Date(previewTemplate.created_at).toLocaleDateString('fr-FR')}
                </div>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handlePrintTemplate(previewTemplate)}
                  >
                    <Printer className="w-4 h-4 mr-2" />
                    Imprimer
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleDownloadPDF(previewTemplate)}
                  >
                    <FileDown className="w-4 h-4 mr-2" />
                    Télécharger
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleShareTemplate(previewTemplate)}
                  >
                    <Share2 className="w-4 h-4 mr-2" />
                    Partager
                  </Button>
                </div>
              </div>
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPreview(false)}>
              Fermer
            </Button>
            <Button onClick={() => {
              setShowPreview(false);
              handleEdit(previewTemplate);
            }}>
              <Edit className="w-4 h-4 mr-2" />
              Modifier
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog de partage */}
      <Dialog open={showShareDialog} onOpenChange={setShowShareDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Share2 className="w-5 h-5 text-green-600" />
              Partager le template
            </DialogTitle>
            <DialogDescription>
              {shareTemplate?.name}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {/* Partage par lien */}
            <div className="space-y-2">
              <Label className="flex items-center gap-2">
                <ExternalLink className="w-4 h-4" />
                Lien de partage
              </Label>
              <div className="flex gap-2">
                <Input 
                  value={`${window.location.origin}/templates/expert/${shareTemplate?.id}`}
                  readOnly
                  className="text-xs"
                />
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={handleCopyShareLink}
                >
                  <Copy className="w-4 h-4" />
                </Button>
              </div>
            </div>
            
            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <span className="w-full border-t" />
              </div>
              <div className="relative flex justify-center text-xs uppercase">
                <span className="bg-white px-2 text-gray-500">ou par email</span>
              </div>
            </div>
            
            {/* Partage par email */}
            <div className="space-y-2">
              <Label htmlFor="shareEmail" className="flex items-center gap-2">
                <Mail className="w-4 h-4" />
                Adresse email
              </Label>
              <Input 
                id="shareEmail"
                type="email"
                value={shareEmail}
                onChange={(e) => setShareEmail(e.target.value)}
                placeholder="destinataire@example.com"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="shareMessage" className="flex items-center gap-2">
                <MessageSquare className="w-4 h-4" />
                Message (optionnel)
              </Label>
              <Textarea 
                id="shareMessage"
                value={shareMessage}
                onChange={(e) => setShareMessage(e.target.value)}
                rows={3}
              />
            </div>
          </div>
          
          <DialogFooter className="flex gap-2">
            <Button variant="outline" onClick={() => setShowShareDialog(false)}>
              Annuler
            </Button>
            <Button 
              onClick={handleSendShare}
              className="bg-green-600 hover:bg-green-700"
            >
              <Mail className="w-4 h-4 mr-2" />
              Envoyer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
